import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectClinicId, selectCanUseGrow } from "../store/slices/authSlice";
import { subscribeToActiveGrowLinks } from "../firebase";
import { growApi, invalidateFinanceAfterGrow } from "../store/services/growApi";

/**
 * useGrowLinksRealtime — keeps RTK caches honest when Grow pays a link.
 *
 * Server-side callbacks (growNotifyCallback) flip links → paid and
 * transactions → paid without any client action, so RTK Query never learns
 * about it. This hook listens (onSnapshot) to the clinic's ACTIVE links
 * (created/sent). When a link leaves that set (paid / cancelled / expired) or
 * a new one appears, it invalidates finance + calendar + GrowLink tags so the
 * Kanban, patient finance tab and appointment modal refetch.
 *
 * Mount once per finance-aware page (FinancePage, patient page finance tab,
 * appointment modal host) — Phase 5 decides where. Unmounted automatically
 * when the user cannot use Grow (not integrated / no plan flag / no permission).
 *
 * @param {{ enabled?: boolean }} [options]
 */
export default function useGrowLinksRealtime({ enabled = true } = {}) {
  const dispatch = useDispatch();
  const clinicId = useSelector(selectClinicId);
  const canUseGrow = useSelector(selectCanUseGrow);
  const prevIdsRef = useRef(null);

  useEffect(() => {
    if (!enabled || !clinicId || !canUseGrow) return undefined;
    prevIdsRef.current = null;

    const unsubscribe = subscribeToActiveGrowLinks(clinicId, (links) => {
      const ids = new Set(links.map((l) => l.id));
      const prev = prevIdsRef.current;
      prevIdsRef.current = ids;
      if (prev === null) return; // initial snapshot — caches are fresh already

      const left = [...prev].filter((id) => !ids.has(id)); // paid / cancelled / expired
      const added = [...ids].filter((id) => !prev.has(id)); // created by a colleague / auto-send
      if (left.length === 0 && added.length === 0) return;

      const patientIds = links.map((l) => l.patientId);
      dispatch(
        growApi.util.invalidateTags([
          { type: "GrowLink", id: "LIST" },
          ...left.map((id) => ({ type: "GrowLink", id })),
          ...added.map((id) => ({ type: "GrowLink", id })),
        ])
      );
      // A link that LEFT the active set is the only case that moves money;
      // invalidate finance caches then (patient-scoped tags for the ones we still know).
      if (left.length > 0) invalidateFinanceAfterGrow(dispatch, patientIds);
    });

    return () => {
      unsubscribe();
      prevIdsRef.current = null;
    };
  }, [enabled, clinicId, canUseGrow, dispatch]);
}
